import type { Icon as PhosphorIcon } from "@phosphor-icons/react"
import type { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { KpiTile } from "../../parts/KpiTile"
import { Delta } from "./AnalyticsCard"

export type ChangeEntry = {
  sign: "up" | "down" | "flat"
  text: string
  /** Translation key for label (e.g. "analytics.kpis.vsPrevMonth"). Optional. */
  labelKey?: string
  /** Fallback label if labelKey missing. */
  label?: string
}

type Props = {
  icon: PhosphorIcon
  label: string
  /** @dehub/help `field` key for the «?» after the label. */
  helpKey?: string
  value: string
  unit?: string
  changes?: ChangeEntry[]
  /** The value is a name, not a figure (e.g. the top-margin product). */
  textValue?: boolean
}

const VS_PREV_YEAR = "analytics.kpis.vsPrevYear"
const ZERO = /^0(\.0+)?%?$/

// The overview KPI card (parts/KpiTile) fed with the analytics change lines.
// Same wording rules as the overview row: no figure → «—» alone (never
// "— өмнөх сараас"), no change → «Өмнөх сартай ижил», arrows stay neutral.
export function KpiCard({ icon, label, helpKey, value, unit, changes, textValue }: Props) {
  const { t } = useTranslation(["dashboard"])

  const lines = (changes ?? []).flatMap((c, i): ReactNode[] => {
    const isYear = c.labelKey === VS_PREV_YEAR
    if (c.text === "—") {
      // A missing year-on-year figure is dropped; a missing month figure keeps the row as «—».
      return isYear ? [] : ["—"]
    }
    if (c.sign === "flat" && ZERO.test(c.text) && c.labelKey) {
      return [t(isYear ? "analytics.kpis.samePrevYear" : "analytics.kpis.samePrevMonth")]
    }
    const caption = c.labelKey ? t(c.labelKey, { defaultValue: c.label ?? "" }) : c.label
    return [
      <span key={i} style={{ display: "contents" }}>
        <Delta sign={c.sign} text={c.text} />
        {caption}
      </span>,
    ]
  })

  return (
    <KpiTile
      icon={icon}
      label={label}
      helpKey={helpKey}
      value={value}
      unit={unit}
      textValue={textValue}
      foot={lines}
    />
  )
}
